import Marionette from "backbone.marionette";
import Radio from "backbone.radio";

import { htmlEscape, errorParser } from "../../../utils";

import ChosenBehavior from "../../../behaviors/chosen";
import DatePickerBehavior from "../../../behaviors/kendo-ui/datepicker";

import NotesCollection from "../../../collections/notes";
import ChartModel from "../../../models/chart-reviews/chart";
import Member from "../../../models/chart-reviews/member";
import Provider from "../../../models/chart-reviews/provider";
import templates from "../../../utils/templates";
import simpleAlert from "../../../utils/alerts/simple";

const DEFAULTS = {
  tagName: "form",
  template: templates.find("chart_reviews", "chart_create_chart"),
};

const globalChannel = Radio.channel("global");
const reportChannel = Radio.channel("report");

class View extends Marionette.ItemView {
  constructor(options = {}) {
    _.defaults(options, DEFAULTS);
    super(options);
  }

  behaviors() {
    return [
      { behaviorClass: ChosenBehavior },
      { behaviorClass: DatePickerBehavior },
    ];
  }

  ui() {
    return {
      // dropdowns
      project: "#projectId",
      retrievalStatus: "#retrievalStatus",
      retrievalStatusNAReason: "#retrievalStatusNAReason",
      reviewers: "#reviewers",
      // text inputs
      receivedDate: "#receivedDate",
      customerRefNumber: "#customerRefNumber",
      providerGroupNumber: "#providerGroupNumber",
      note: "#note",
      // buttons
      selectMember: "button.select-member",
      selectProvider: "button.select-provider",
      saveExitBtn: "button.save-exit",
      saveOpenBtn: "button.save-open",
    };
  }

  events() {
    return {
      "change @ui.project": "_onSingleSelection",
      "change @ui.retrievalStatus": "_onSingleSelection",
      "change @ui.retrievalStatusNAReason": "_onSingleSelection",
      "change @ui.receivedDate": "_onDatePickerChange",
      "change @ui.customerRefNumber": "_onInput",
      "keyup @ui.providerGroupNumber": "_uppercase",
      "change @ui.providerGroupNumber": "_onInput",
      "change @ui.reviewers": "_onMultiSelection",
      "change @ui.note": "_onInput",
      "click @ui.selectMember": "_onSelectMember",
      "click @ui.selectProvider": "_onSelectProvider",
      "click @ui.saveOpenBtn": "_openOnCreate",
      reset: "_onReset",
      submit: "_onSubmit",
    };
  }

  initialize() {
    this.emptyFormModel = this.model.toJSON();
    this.openChartOnCreate = false;

    // make sure to render the view when the model changes
    this.listenTo(this.model, "change", this._onModelChange);

    // setup listeners for member and provider selection
    this.listenTo(
      reportChannel,
      "chart-reviews.member:selected",
      this._onMemberSelected
    );
    this.listenTo(
      reportChannel,
      "chart-reviews.provider:selected",
      this._onProviderSelected
    );
  }

  onAttach() {
    this._notifyBehaviors();
  }

  _onModelChange() {
    this.render();
    this._notifyBehaviors();
  }

  _notifyBehaviors() {
    this.triggerMethod("ContentRendered");
  }

  _onSelectMember() {
    globalChannel.trigger("chart-reviews.member:search");
  }

  _onSelectProvider() {
    globalChannel.trigger("chart-reviews.provider:search");
  }

  // handles single select dropdowns
  _onSingleSelection(e) {
    const $el = $(e.target);
    const name = $el[0].name;
    const text = $el.val().trim();

    try {
      const listName = `${name}List`;
      this.model.get(listName).map((item) => {
        const match = text == item.getKey();
        item.setChecked(match);
        return item;
      });
    } catch (err) {
      // do nothing
    }

    // update property on model
    this.model.set(name, text);
  }

  // handles multi-select dropdowns
  _onMultiSelection(e) {
    const $el = $(e.target);
    const name = `${$el[0].name}List`;
    const text = $el.val();

    const coll = this.model.get(name);
    _.each(text, (id) => {
      const match = coll.get(id);
      if (match) {
        match.setChecked(true);
      }
    });
  }

  // handles plain text input fields
  _onInput(e) {
    const $el = $(e.target);
    const name = $el[0].name;
    const text = $el.val().trim();

    // update model
    this.model.set(name, text);
  }

  // handle date picker inputs
  _onDatePickerChange(e) {
    const $el = $(e.target);
    const name = $el[0].name;
    let text = $el.val().trim();
    if ("month/day/year" === text) {
      text = null;
    }

    // update model
    this.model.set(name, text);
  }

  _uppercase(e) {
    const $el = $(e.target);
    const text = $el.val().trim().toUpperCase();

    // update form field
    $el.val(text);
  }

  _onMemberSelected(member) {
    // fix for mislabeled field on DT result
    if (_.has(member, "memberId")) {
      member["id"] = member.memberId;
      delete member.memberId;
    }

    // simplify member object
    const data = _.mapObject(member, (val) => val.value);
    const i = new Member(data);
    this.model.setSelectedMember(i);

    // show success message
    globalChannel.trigger("status:show", {
      autoDismissTimeout: 2000,
      type: "info",
      message: `Member # ${i.getMemberNumber()} has been associated with this chart.`,
    });
  }

  _onProviderSelected(provider) {
    // fix for mislabeled fields on DT result
    if (_.has(provider, "providerId")) {
      provider["id"] = provider.providerId;
      delete provider.providerId;
    }
    if (_.has(provider, "npi")) {
      provider["nationalProviderId"] = provider.npi;
      delete provider.npi;
    }

    // simplify provider object
    const data = _.mapObject(provider, (val) => val.value);
    const i = new Provider(data);
    this.model.setSelectedProvider(i);

    // show success message
    globalChannel.trigger("status:show", {
      autoDismissTimeout: 2000,
      type: "info",
      message: `Provider # ${i.getProviderNumber()} has been associated with this chart.`,
    });
  }

  // overrides the default reset behavior. it resets the model instead of the
  // markup.
  _onReset(e) {
    e.preventDefault();

    // reset model with setup data
    this.model.set(this.emptyFormModel);
  }

  // updates the return route
  _openOnCreate() {
    this.openChartOnCreate = true;
    this.$el.submit();
  }

  _onSubmit(e) {
    e.preventDefault();

    // extract submitted form data
    const formData = Backbone.Syphon.serialize(this);

    // When a note is provided, create a collection of one note
    if (_.has(formData, "note") && !_.isEmpty(formData.note)) {
      const notes = new NotesCollection({
        note: htmlEscape(formData.note),
      });
      formData.notes = notes.toJSON();
    }
    // remove from form data when empty
    delete formData.note;

    // remove receivedDate if left empty (and placeholder text is submitted)
    if ("month/day/year" === formData.receivedDate) {
      delete formData.receivedDate;
    }

    // cast member and provider to number
    formData.member = +formData.member || null;
    formData.provider = +formData.provider || null;
    formData.projectId = +formData.projectId || null;
    formData.providerGroupNumber =
      formData.providerGroupNumber.toUpperCase() || null;

    // cast reviewer IDs to numbers
    formData.reviewers = formData.reviewers || [].map((id) => +id);

    // create chart model instance with cleansed form data
    const model = new ChartModel(formData);

    // validate it
    if (model.isValid()) {
      // attempt to create chart
      model.save(model.attrs, {
        success: this._onSync.bind(this),
        error: this._onSyncFailure.bind(this),
      });
    } else {
      // show warning message
      globalChannel.trigger("status:show", {
        type: "warning",
        message: model.validationError,
      });
    }
  }

  _onSync(model) {
    // show success message
    globalChannel.trigger("status:show", {
      type: "success",
      message: "Your chart was created successfully.",
    });

    // load DRG Edit if requested
    if (this.openChartOnCreate) {
      const { id, statusValue } = model.toJSON();
      globalChannel.trigger("chart-reviews-created", {
        id,
        statusValue,
      });
    }

    // dismiss modal
    this.triggerMethod("dismiss:modal");
  }

  _onSyncFailure(model, response) {
    let message = errorParser(response);
    try {
      message = response.responseJSON.error["error-message"];
    } catch (e) {
      console.warn("Unable to find additional info on thrown exception.");
    }

    // show error
    simpleAlert({
      type: "error",
      title: "Your changes could not be saved.",
      text: message,
    });
  }
}

export { View as default };
